import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { ClientSubscription as SubscriptionType, SubscriptionPlan } from '../../types';
import BkashPaymentModal from '../../components/payment/BkashPaymentModal';

const ClientSubscription: React.FC = () => {
  const [subscription, setSubscription] = useState<SubscriptionType | null>(null);
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [allPlans, setAllPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    loadSubscription();
    loadUserProfile();
  }, []);

  const loadSubscription = async () => {
    setLoading(true);

    // Load subscription details
    const subResponse = await api.getClientSubscription();
    if (subResponse.success && subResponse.data) {
      setSubscription(subResponse.data);
      setPlan(subResponse.data.plan);
    }

    // Load all plans for comparison
    const plansResponse = await api.getPublicPlans();
    if (plansResponse.success && plansResponse.data) {
      setAllPlans(plansResponse.data);
    }

    setLoading(false);
  };

  const loadUserProfile = async () => {
    const response = await api.getProfile();
    if (response.success && response.data) {
      setUser(response.data);
    }
  };

  const handleUpgradeClick = (upgradePlan: SubscriptionPlan) => {
    setSelectedPlan(upgradePlan);
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = async (_result: {
    paymentId: string;
    trxId: string;
    subscriptionId: string;
  }) => {
    // Refresh subscription data
    setShowPaymentModal(false);
    setSelectedPlan(null);
    await loadSubscription();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-neutral-500 text-sm">Loading subscription...</p>
        </div>
      </div>
    );
  }

  const daysRemaining = subscription
    ? Math.max(0, Math.ceil((new Date(subscription.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  const isExpiringSoon = daysRemaining > 0 && daysRemaining <= 7;
  const isExpired = daysRemaining === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">My Subscription</h1>
        <p className="text-neutral-500">Manage your plan and view features</p>
      </div>

      {/* Current Plan */}
      {subscription && plan ? (
        <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-2xl p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">💎</span>
                </div>
                <div>
                  <p className="text-neutral-400 text-sm">Current Plan</p>
                  <p className="text-2xl font-bold text-white">{plan.name}</p>
                </div>
              </div>
              <p className="text-neutral-400 text-sm max-w-md">{plan.description}</p>
            </div>

            <div className="flex flex-col items-end gap-4">
              <div className="text-right">
                <p className="text-3xl font-bold text-white">
                  ৳{plan.priceBdt.toLocaleString()}
                  <span className="text-neutral-500 text-lg font-normal">/month</span>
                </p>
              </div>
              <div className={`px-4 py-2 rounded-xl ${
                daysRemaining > 7
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : daysRemaining > 0
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'bg-red-500/20 text-red-400'
              }`}>
                {daysRemaining > 0 ? (
                  <>
                    <span className="font-bold">{daysRemaining}</span>
                    <span className="text-sm ml-1">days remaining</span>
                  </>
                ) : (
                  <span className="font-bold">Expired</span>
                )}
              </div>
            </div>
          </div>

          {/* Renewal Alert */}
          {(isExpiringSoon || isExpired) && (
            <div className={`mt-4 p-4 rounded-xl ${
              isExpired ? 'bg-red-500/10 border border-red-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{isExpired ? '⚠️' : '⏰'}</span>
                  <div>
                    <p className={`font-medium ${isExpired ? 'text-red-400' : 'text-yellow-400'}`}>
                      {isExpired ? 'Your subscription has expired!' : 'Your subscription is expiring soon!'}
                    </p>
                    <p className="text-neutral-400 text-sm">
                      {isExpired ? 'Renew now to continue using GEO-PRO features' : `Only ${daysRemaining} days left. Renew to avoid service interruption.`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleUpgradeClick(plan)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isExpired
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-yellow-500 hover:bg-yellow-600 text-black'
                  }`}
                >
                  Renew Now
                </button>
              </div>
            </div>
          )}

          {/* Subscription Details */}
          <div className="mt-6 pt-6 border-t border-emerald-500/20 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-neutral-500 text-xs uppercase">Status</p>
              <p className={`font-medium ${
                subscription.status === 'active' ? 'text-emerald-400' :
                subscription.status === 'expired' ? 'text-red-400' :
                'text-yellow-400'
              }`}>
                {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
              </p>
            </div>
            <div>
              <p className="text-neutral-500 text-xs uppercase">Started</p>
              <p className="text-white font-medium">
                {new Date(subscription.startsAt).toLocaleDateString('en-BD', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </p>
            </div>
            <div>
              <p className="text-neutral-500 text-xs uppercase">Expires</p>
              <p className="text-white font-medium">
                {new Date(subscription.expiresAt).toLocaleDateString('en-BD', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </p>
            </div>
            <div>
              <p className="text-neutral-500 text-xs uppercase">Payment</p>
              <p className={`font-medium ${
                subscription.paymentStatus === 'paid' ? 'text-emerald-400' :
                subscription.paymentStatus === 'failed' ? 'text-red-400' :
                'text-yellow-400'
              }`}>
                {subscription.paymentStatus.charAt(0).toUpperCase() + subscription.paymentStatus.slice(1)}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">No Active Subscription</h3>
          <p className="text-neutral-400 mb-6">
            You don't have an active subscription. Choose a plan below to get started.
          </p>
        </div>
      )}

      {/* Usage Limits */}
      {plan && (
        <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#262626]">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Usage Limits</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#1a1a1a] rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">📊</span>
                <p className="text-neutral-400">Daily Requests</p>
              </div>
              <p className="text-3xl font-bold text-white">{plan.maxRequestsPerDay}</p>
              <p className="text-neutral-500 text-sm">requests per day</p>
            </div>
            <div className="bg-[#1a1a1a] rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">📈</span>
                <p className="text-neutral-400">Monthly Requests</p>
              </div>
              <p className="text-3xl font-bold text-white">{plan.maxRequestsPerMonth.toLocaleString()}</p>
              <p className="text-neutral-500 text-sm">requests per month</p>
            </div>
          </div>
        </div>
      )}

      {/* Included Features */}
      {plan && (
        <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#262626] flex items-center justify-between">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Included Features</h3>
            <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded">
              {plan.features.length} FEATURES
            </span>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {plan.features.map((feature, idx) => (
              <div
                key={idx}
                className="p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl"
              >
                <div className="flex items-center gap-2">
                  <span className="text-emerald-500">✓</span>
                  <p className="text-white text-sm font-medium">{feature}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Plans Comparison */}
      {allPlans.length > 0 && (
        <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#262626]">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">
              {subscription ? 'Upgrade Your Plan' : 'Choose a Plan'}
            </h3>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {allPlans.map((p) => {
              const isCurrentPlan = plan?.id === p.id;
              const isUpgrade = plan ? p.priceBdt > plan.priceBdt : false;
              const isDowngrade = plan ? p.priceBdt < plan.priceBdt : false;

              return (
                <div
                  key={p.id}
                  className={`p-6 rounded-xl border transition-all ${
                    isCurrentPlan
                      ? 'bg-emerald-500/10 border-emerald-500/30'
                      : 'bg-[#1a1a1a] border-[#262626] hover:border-[#363636]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-white">{p.name}</h4>
                    <div className="flex gap-2">
                      {p.isFeatured && (
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-[10px] font-bold rounded">
                          POPULAR
                        </span>
                      )}
                      {isCurrentPlan && (
                        <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-[10px] font-bold rounded">
                          CURRENT
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white mb-2">
                    ৳{p.priceBdt.toLocaleString()}
                    <span className="text-neutral-500 text-sm font-normal">/mo</span>
                  </p>
                  <p className="text-neutral-500 text-sm mb-4">{p.description}</p>
                  <div className="space-y-2 mb-4">
                    <p className="text-neutral-400 text-xs">
                      <span className="text-white font-medium">{p.maxRequestsPerDay}</span> daily requests
                    </p>
                    <p className="text-neutral-400 text-xs">
                      <span className="text-white font-medium">{p.maxRequestsPerMonth.toLocaleString()}</span> monthly requests
                    </p>
                    <p className="text-neutral-400 text-xs">
                      <span className="text-white font-medium">{p.features.length}</span> features included
                    </p>
                  </div>

                  {/* Features Preview */}
                  <div className="mb-4 max-h-32 overflow-y-auto">
                    <div className="space-y-1">
                      {p.features.slice(0, 5).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs">
                          <span className="text-emerald-500">✓</span>
                          <span className="text-neutral-400">{feature}</span>
                        </div>
                      ))}
                      {p.features.length > 5 && (
                        <p className="text-neutral-500 text-xs mt-1">
                          +{p.features.length - 5} more features
                        </p>
                      )}
                    </div>
                  </div>

                  {!isCurrentPlan && (
                    <button
                      onClick={() => handleUpgradeClick(p)}
                      className={`w-full py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                        isUpgrade
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                          : isDowngrade
                            ? 'bg-[#262626] hover:bg-[#363636] text-neutral-300'
                            : 'bg-pink-500 hover:bg-pink-600 text-white'
                      }`}
                    >
                      <span>💳</span>
                      <span>
                        {isUpgrade ? 'Upgrade Now' : isDowngrade ? 'Downgrade' : 'Subscribe Now'}
                      </span>
                    </button>
                  )}

                  {isCurrentPlan && !isExpired && !isExpiringSoon && (
                    <div className="w-full py-3 bg-emerald-500/10 text-emerald-400 rounded-xl font-medium text-center">
                      Current Plan
                    </div>
                  )}

                  {isCurrentPlan && (isExpired || isExpiringSoon) && (
                    <button
                      onClick={() => handleUpgradeClick(p)}
                      className={`w-full py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                        isExpired
                          ? 'bg-red-500 hover:bg-red-600 text-white'
                          : 'bg-yellow-500 hover:bg-yellow-600 text-black'
                      }`}
                    >
                      <span>🔄</span>
                      <span>Renew Now</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
            <span className="text-2xl">💬</span>
          </div>
          <div>
            <h3 className="font-bold text-white mb-1">Need Help with Payments?</h3>
            <p className="text-blue-400 text-sm">
              If you're experiencing issues with bKash payments or need alternative payment methods,
              contact your reseller or our support team. We accept bKash, Nagad, and bank transfers.
            </p>
          </div>
        </div>
      </div>

      {/* bKash Payment Modal */}
      {selectedPlan && (
        <BkashPaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPlan(null);
          }}
          plan={selectedPlan}
          clientData={user ? {
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            phone: user.phone,
            companyName: user.companyName,
          } : undefined}
          onPaymentComplete={handlePaymentComplete}
        />
      )}
    </div>
  );
};

export default ClientSubscription;
